import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import OutlinedSelect from '../../../../Components/OutlinedSelect';
import { Grid, Typography, Button, CircularProgress, TextField, Switch, Radio, RadioGroup, Checkbox, Chip, FormHelperText } from '@material-ui/core/';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Autocomplete } from '@material-ui/lab';
import PropTypes from 'prop-types';
import { initAssignLicenseFields, isRowSelected, LABS_TAB } from '../AssignLicence';
import LicenceTabsPanel, { LicenceRow, AllocatedCell, RowDeleteButton } from '../LicenceTabsPanel';
import LearningAccessSection from '../LearningAccessSection';
import PhoneInputField from '../../../../Components/PhoneInputField';
import { UserServices } from '../../../../Services/UserServices';
import { checkValidEmail, IsNumeric, notify } from 'utils';
import { ERROR_MESSAGE, USER_ROLES } from 'utils/constants';
import { formatApiErrorMessage } from '../../../../utils/userProfileFields';
import { YOUTH_PROGRAMME } from '../../../../utils/programmeOptions';

const userServices = new UserServices();

const PROJECT_LICENCE_COLUMNS = {
    assessments: [['Assessment', 7], ['Licences', 2], ['Allocated', 2], ['', 1]],
    courses: [['Provider', 2], ['Category', 3], ['Course', 3], ['Licences', 2], ['Allocated', 1], ['', 1]],
    apprenticeships: [['Apprenticeship', 7], ['Licences', 2], ['Allocated', 2], ['', 1]],
};

const ID_FIELD = { assessments: 'assessment_id', courses: 'course_id', apprenticeships: 'apprenticeship_id' };

const getOptionId = (option) => option?.id || option?.uuid;

const findOption = (options, value) => (value && (options || []).find(opt => getOptionId(opt) === value)) || null;

const findOptionName = (options, value) => findOption(options, value)?.name || '';

const providersFromCourses = (courseList) => (
    [...new Set((courseList || []).map(course => course?.provider).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
);

const providersForCategory = (courseList, categoryName) => {
    const category = (categoryName || '').trim();
    const providers = providersFromCourses(courseList);
    if (!category) return providers;
    const names = [...new Set(
        (courseList || [])
            .filter(course => (course?.category || '').trim() === category)
            .map(course => course?.provider)
            .filter(Boolean)
    )];
    const ordered = providers.filter(provider => names.includes(provider));
    return ordered.length ? ordered : names.sort((a, b) => a.localeCompare(b));
};

const categoriesForProvider = (courseList, provider) => {
    const source = provider
        ? (courseList || []).filter(course => course?.provider === provider)
        : (courseList || []);
    return [...new Set(source.map(course => (course?.category || '').trim()).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
};

const withCurrentOption = (options, current) => {
    const value = typeof current === 'string' ? current.trim() : '';
    if (value && !options.includes(value)) return [value, ...options];
    return options;
};

const courseOptionsForFilters = (courseList, item) => {
    const selectedCategory = (item?.category || '').trim();
    const selectedProvider = item?.provider || '';
    return (courseList || []).filter(course => {
        if (selectedProvider && course?.provider !== selectedProvider) return false;
        if (selectedCategory && (course?.category || '').trim() !== selectedCategory) return false;
        return true;
    });
};

export const nextCourseFilterRow = (courseList, current = {}, field, value) => {
    let nextProvider = field === 'provider' ? (value || '') : (current.provider || '');
    let nextCategory = field === 'category' ? (value || '').trim() : (current.category || '').trim();

    if (field === 'provider') {
        const categoryStillApplies = !nextCategory
            || !nextProvider
            || providersForCategory(courseList, nextCategory).includes(nextProvider);
        if (!categoryStillApplies) nextCategory = '';
    }

    if (field === 'category') {
        const matchingProviders = nextCategory ? providersForCategory(courseList, nextCategory) : [];
        if (nextCategory && matchingProviders.length === 1) {
            nextProvider = matchingProviders[0];
        } else if (nextCategory && nextProvider && !matchingProviders.includes(nextProvider)) {
            nextProvider = '';
        }
    }

    const selected = (courseList || []).find(course => getOptionId(course) === current.course_id);
    const categoryMatches = field === 'category'
        ? Boolean(selected) && (selected.category || '').trim() === nextCategory
        : !nextCategory || (selected?.category || '').trim() === nextCategory;
    const providerMatches = !nextProvider || selected?.provider === nextProvider;
    const keepCourse = Boolean(current.course_id && selected) && categoryMatches && providerMatches;

    if (keepCourse) {
        return { ...current, provider: nextProvider, category: nextCategory };
    }

    return {
        ...initAssignLicenseFields.courses[0],
        provider: nextProvider,
        category: nextCategory,
    };
};

const ProjectCountInput = ({ item, index, action, disabled, handleChange }) => (
    <TextField
        variant="outlined"
        size="small"
        fullWidth
        label="Licences"
        value={item.count}
        type="text"
        name="count"
        disabled={disabled}
        inputProps={{ max: item?.maxCount }}
        onChange={evt => {
            if (evt.target.value && !IsNumeric(evt.target.value)) {
                return null;
            }
            if (evt.target.value && evt.target.value > item?.maxCount) {
                notify("error", `You can't assign more then ${item?.maxCount}`);
                return null;
            }
            handleChange(evt, index, action);
        }}
    />
);

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        height: 180,
        background: '#FFFFFF',
        boxShadow: "0px 4px 30px rgba(12, 41, 84, 0.1)",
        borderRadius: 5
    },
    labelStyle: { 
        marginBottom: '20px', 
        marginTop: '15px', 
        color: '#333333' 
    },
    btn: {
        padding: '8px 22px',
        fontSize: '0.875rem',
        color: 'white',
        borderRadius: '5px',
        minWidth: 120,
        [theme.breakpoints.down('xs')]: {
            minWidth: 96,
            padding: '8px 14px',
        },
    },
    cancelBtn: {
        padding: '8px 22px',
        background: '#B7B7B7',
        fontSize: '0.875rem',
        color: 'white',
        borderRadius: '5px',
        minWidth: 120,
        "&:hover": {
            background: "#454343"
        },
        [theme.breakpoints.down('xs')]: {
            minWidth: 96,
            padding: '8px 14px',
        }
    },
    placeBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        gap: theme.spacing(1),
        boxSizing: 'border-box',
        padding: '20px 0',
        marginTop: 20,
        width: '100%',
    },
    placeBtnEnd: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginLeft: 'auto',
        '& > *': {
            marginLeft: 0,
        },
    },
    title: {
        fontSize: 15,
        fontWeight: 700,
        lineHeight: "18px",
        color: '#4D5766'
    },
    pos: {
        marginBottom: 12,
    },
    recruitmentBanner: {
        marginTop: 8,
        marginBottom: 4,
        padding: '10px 16px',
        borderRadius: 6,
        backgroundColor: 'rgba(12, 41, 84, 0.06)',
        border: '1px solid rgba(12, 41, 84, 0.18)',
    },
    recruitmentBannerTitle: {
        fontWeight: 600,
        fontSize: '0.9rem',
        color: '#0C2954',
    },
    recruitmentBannerSubtitle: {
        fontSize: '0.8rem',
        marginTop: 2,
        color: '#4D5766',
    },
    adminPanel: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        padding: theme.spacing(2),
        border: '1px solid #d7e0ea',
        borderRadius: 8,
        backgroundColor: '#fff',
    },
    adminTitle: {
        color: '#1f2937',
        fontSize: '0.9375rem',
        fontWeight: 700,
        marginBottom: theme.spacing(0.5),
    },
    adminOption: {
        marginRight: theme.spacing(3),
        marginBottom: theme.spacing(0.5),
        '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem',
            color: '#374151',
        },
    },
    radio: {
        padding: 6,
        color: theme.palette.primary.main,
        backgroundColor: 'transparent !important',
        '&:hover': {
            backgroundColor: 'rgba(61, 125, 221, 0.08) !important',
        },
        '&.Mui-checked': {
            color: theme.palette.primary.main,
            backgroundColor: 'transparent !important',
        },
    },
    inviteFields: {
        marginTop: theme.spacing(1),
    },
    programmeSection: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    programmeLabel: {
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '0.8125rem',
        fontWeight: 700,
        marginBottom: theme.spacing(1),
    },
    programmeCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(1.5),
        [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
    },
    programmeCard: {
        alignItems: 'flex-start',
        margin: 0,
        padding: theme.spacing(1.5),
        border: '1px solid var(--dm-border, #d7e0ea)',
        borderRadius: 12,
        backgroundColor: 'var(--contentColor, #fff)',
        transition: 'border-color 150ms ease-out, background-color 150ms ease-out',
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
        '&:has(.Mui-checked)': {
            borderColor: theme.palette.primary.main,
            backgroundColor: 'rgba(61, 125, 221, 0.1)',
        },
        '&.Mui-disabled:hover': {
            borderColor: 'var(--dm-border, #d7e0ea)',
        },
    },
    programmeCardLocked: {
        cursor: 'not-allowed',
        '&, &:hover, &:has(.Mui-checked)': {
            borderColor: 'var(--dm-border, #d7e0ea)',
        },
        '& .MuiFormControlLabel-label.Mui-disabled': {
            color: 'inherit',
        },
    },
    programmeCardLockedCurrent: {
        '&, &:has(.Mui-checked)': {
            backgroundColor: 'var(--dm-row-alt, #f4f6f9)',
        },
    },
    programmeCardLockedOther: {
        opacity: 0.45,
        '&, &:hover': {
            backgroundColor: 'transparent',
            borderStyle: 'dashed',
        },
    },
    radioLocked: {
        '&, &.Mui-checked, &.Mui-disabled': {
            color: 'var(--dm-muted, #8a97a8)',
        },
        '&:hover': {
            backgroundColor: 'transparent !important',
        },
    },
    programmeLabelRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
        '& $programmeLabel': {
            marginBottom: 0,
        },
    },
    lockedBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: '0.6875rem',
        fontWeight: 700,
        color: 'var(--dm-muted, #647184)',
        backgroundColor: 'var(--dm-row-alt, #eef1f5)',
        border: '1px solid var(--dm-border, #e1e7ee)',
        '& svg': { fontSize: 13 },
    },
    lockedNote: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        '& svg': { fontSize: 15, flexShrink: 0 },
    },
    programmeCardText: {
        display: 'block',
        paddingTop: 4,
    },
    programmeCardTitle: {
        display: 'block',
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '0.875rem',
        fontWeight: 700,
    },
    programmeCardDescription: {
        display: 'block',
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.75rem',
        lineHeight: 1.45,
        marginTop: 3,
    },
    programmeHelper: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.75rem',
        lineHeight: 1.45,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2.5),
    },
    programmeHint: {
        padding: theme.spacing(1, 1.5),
        marginBottom: theme.spacing(1.5),
        borderRadius: 6,
        color: '#765400',
        backgroundColor: '#fff7df',
        fontSize: '0.75rem',
    },
}));

const emptyInvite = {
    firstName: '',
    lastName: '',
    email: '',
    location: '',
    contact_no: '',
};

const CreateProjectForm = forwardRef(({
    handleInputChange,
    formState,
    customerList,
    customerAdminList,
    projectAdminList,
    project_id,
    customerId,
    onProjectAdminCreated,
    onDraftChange,
    handleCancelCreate,
    formValidationCheck,
    loading,
    handleBack,
    assignLicenseForm,
    handleChangeAsl,
    handleAddAssignLicence,
    handleRemoveAssignLicence,
    assessmentList,
    apprenticeshipList,
    courseList,
    learningAccess,
    programmeOptions,
    fieldErrors = {},
    pendingLearningAccess = null,
    section = 'all'
}, ref) => {

    const classes = useStyles();
    const showDetails = section !== 'licences';
    const showLicences = section !== 'details';
    const showActions = section === 'all';
    const [adminMode, setAdminMode] = useState('existing');
    const [invite, setInvite] = useState(emptyInvite);
    const adminModeRef = useRef('existing');
    const inviteRef = useRef(emptyInvite);
    const createdAdminId = useRef('');
    const projectAdminRef = useRef(formState.project_admin);
    adminModeRef.current = adminMode;
    inviteRef.current = invite;
    projectAdminRef.current = formState.project_admin;
    const role = localStorage.getItem('role');
    const isSuperAdmin = role === USER_ROLES.SUPER_ADMIN;
    const canViewLearningAccess = isSuperAdmin
        || role === USER_ROLES.CUSTOMER_ADMIN
        || role === USER_ROLES.PROJECT_ADMIN;
    const showLearningAccess = !!project_id && !!learningAccess && canViewLearningAccess;
    const showPendingLearningAccess = !project_id && !!pendingLearningAccess && isSuperAdmin;
    const programmeTypes = programmeOptions?.programmeTypes || [];
    const programmeSubtypes = programmeOptions?.programmeSubtypes || [];
    const deliveryProvinces = programmeOptions?.deliveryProvinces || [];
    const deliveryModes = programmeOptions?.deliveryModes || [];
    const programmeLocked = !!formState.programme_type && !!formState.programme_type_locked;
    const selectedProvinces = deliveryProvinces.filter(option => (
        (formState.delivery_provinces || []).includes(option.code)
    ));

    const markDraft = () => {
        if (onDraftChange) onDraftChange();
    };

    const handleAdminMode = (event) => {
        setAdminMode(event.target.value);
        markDraft();
    };

    const handleInviteChange = (event) => {
        const { name, value } = event.target;
        createdAdminId.current = '';
        setInvite(prev => ({ ...prev, [name]: value }));
        markDraft();
    };

    useImperativeHandle(ref, () => ({
        resolveProjectAdmin: async () => {
            if (!showDetails || adminModeRef.current !== 'invite') {
                return { ok: true, id: projectAdminRef.current || '' };
            }
            if (createdAdminId.current) {
                return { ok: true, id: createdAdminId.current };
            }
            const draft = inviteRef.current;
            const email = draft.email.trim();
            if (
                !draft.firstName.trim()
                || !draft.lastName.trim()
                || !email
                || !draft.location.trim()
                || !draft.contact_no
            ) {
                notify('error', 'Enter the new project administrator’s first name, surname, work email, mobile number and location.');
                return { ok: false };
            }
            if (!checkValidEmail(email)) {
                notify('error', ERROR_MESSAGE.VALID_EMAIL);
                return { ok: false };
            }
            if (!customerId) {
                notify('error', 'Save the customer before adding a project admin.');
                return { ok: false };
            }
            const payload = {
                firstName: draft.firstName.trim(),
                lastName: draft.lastName.trim(),
                email,
                userName: email,
                location: draft.location.trim(),
                contact_no: draft.contact_no,
                role: USER_ROLES.PROJECT_ADMIN,
                is_active: true,
                customers: [customerId],
            };
            if (project_id) payload.project_id = project_id;
            const result = await userServices.addUser(payload);
            if (result && result.status) {
                const createdUser = result.data || {};
                const newAdmin = {
                    name: createdUser.userName || email,
                    id: createdUser.uuid || createdUser.id || '',
                };
                createdAdminId.current = newAdmin.id;
                setAdminMode('existing');
                notify('success', result.message || 'Project admin created successfully.');
                if (onProjectAdminCreated) await onProjectAdminCreated(newAdmin, email);
                return { ok: true, id: newAdmin.id, created: true };
            }
            notify('error', formatApiErrorMessage(result, 'Project admin could not be created.'));
            return { ok: false };
        },
    }));

    const optionLists = {
        assessments: assessmentList,
        courses: courseList,
        apprenticeships: apprenticeshipList,
    };

    const licenceTabs = [
        { key: 'assessments', label: 'Assessments', singular: 'assessment' },
        { key: 'courses', label: 'Courses', singular: 'course', alignCourses: true },
        { key: 'apprenticeships', label: 'Apprenticeships', singular: 'apprenticeship' },
    ].map(tab => ({
        ...tab,
        canAdd: true,
        addDisabled: assignLicenseForm[tab.key].length >= (optionLists[tab.key] || []).length,
        columns: PROJECT_LICENCE_COLUMNS[tab.key],
    })).concat(LABS_TAB);

    const getRowLabel = (type, item) => {
        if (type === 'labs') return item?.name || '';
        if (type === 'courses') {
            return `${findOptionName(courseList, item?.course_id)} ${item?.provider || ''} ${item?.category || ''}`;
        }
        return findOptionName(optionLists[type], item?.[ID_FIELD[type]]);
    };

    const renderSelectableRow = (type, item, key) => {
        const idField = ID_FIELD[type];
        const singular = type === 'courses' ? 'course' : type === 'assessments' ? 'assessment' : 'apprenticeship';
        return (
            <LicenceRow>
                <Grid item xs={12} md={7}>
                    <Autocomplete
                        id={`${type}-${key}`}
                        value={findOption(optionLists[type], item[idField])}
                        options={optionLists[type] || []}
                        getOptionLabel={option => option?.name || ''}
                        getOptionSelected={(option, selected) => getOptionId(option) === getOptionId(selected)}
                        onChange={(event, newValue) => {
                            const value = newValue ? getOptionId(newValue) : '';
                            let check = assignLicenseForm[type]?.find(cr => cr?.[idField] === value && value);
                            if (check) {
                                notify('error', `This ${singular} is already selected!`);
                                return null;
                            }
                            handleChangeAsl({ target: { name: idField, value } }, key, type);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                fullWidth
                                label={`Select ${singular.charAt(0).toUpperCase()}${singular.slice(1)}`}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={5} md={2}>
                    <ProjectCountInput
                        item={item}
                        index={key}
                        action={type}
                        disabled={!item[idField]}
                        handleChange={handleChangeAsl}
                    />
                </Grid>
                <Grid item xs={5} md={2}>
                    <AllocatedCell count={item?.user_count} total={item?.count} />
                </Grid>
                <RowDeleteButton action={type} index={key} label={singular} handleClick={handleRemoveAssignLicence} hidden={parseInt(item?.user_count, 10) > 0} />
            </LicenceRow>
        );
    };

    const renderCourseRow = (item, key) => {
        const takenCourseIds = new Set(
            (assignLicenseForm.courses || [])
                .filter((course, index) => index !== key && course?.course_id)
                .map(course => course.course_id)
        );
        const isCourseTaken = (course) => [course?.uuid, course?.id].some(id => id && takenCourseIds.has(id));
        const selectableCourses = (courseList || []).filter(course => !isCourseTaken(course));
        const providerOptions = withCurrentOption(
            item.category ? providersForCategory(selectableCourses, item.category) : providersFromCourses(selectableCourses),
            item.provider
        );
        const categoryOptions = withCurrentOption(categoriesForProvider(selectableCourses, item.provider), item.category);
        const filteredCourses = courseOptionsForFilters(selectableCourses, item);
        const selectedCourse = findOption(courseList, item.course_id);
        const courseOptions = selectedCourse && !filteredCourses.some(course => getOptionId(course) === getOptionId(selectedCourse))
            ? [selectedCourse, ...filteredCourses]
            : filteredCourses;

        return (
            <LicenceRow>
                <Grid item xs={12} md={2}>
                    <Autocomplete
                        key={`provider-${key}-${item.category || ''}`}
                        id={`provider-${key}`}
                        value={item.provider || null}
                        options={providerOptions}
                        getOptionLabel={(option) => option || ''}
                        onChange={(event, newValue) => {
                            handleChangeAsl({ target: { name: 'provider', value: newValue || '' } }, key, 'courses');
                        }}
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" size="small" fullWidth label="Select Provider" />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Autocomplete
                        id={`category-${key}`}
                        value={item.category || null}
                        options={categoryOptions}
                        getOptionLabel={(option) => option || ''}
                        onChange={(event, newValue) => {
                            const categoryName = (typeof newValue === 'string' ? newValue : '').trim();
                            handleChangeAsl({ target: { name: 'category', value: categoryName } }, key, 'courses');
                        }}
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" size="small" fullWidth label="Select Category" />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Autocomplete
                        key={`course-${key}-${item.provider || ''}-${item.category || ''}`}
                        id={`course-${key}`}
                        value={selectedCourse}
                        options={courseOptions}
                        getOptionLabel={option => option?.name || ''}
                        getOptionSelected={(option, selected) => getOptionId(option) === getOptionId(selected)}
                        onChange={(event, newValue) => {
                            const value = newValue ? getOptionId(newValue) : '';
                            const alreadySelected = (assignLicenseForm.courses || []).some((course, index) => (
                                index !== key && value && course?.course_id && (
                                    course.course_id === value || course.course_id === newValue?.uuid || course.course_id === newValue?.id
                                )
                            ));
                            if (alreadySelected) return null;
                            handleChangeAsl({ target: { name: 'course_id', value } }, key, 'courses');
                        }}
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" size="small" fullWidth label="Select Course" />
                        )}
                    />
                </Grid>
                <Grid item xs={5} md={2}>
                    <ProjectCountInput
                        item={item}
                        index={key}
                        action="courses"
                        disabled={!item.course_id}
                        handleChange={handleChangeAsl}
                    />
                </Grid>
                <Grid item xs={5} md={1}>
                    <AllocatedCell count={item?.user_count} total={item?.count} />
                </Grid>
                <RowDeleteButton action="courses" index={key} label="course" handleClick={handleRemoveAssignLicence} hidden={parseInt(item?.user_count, 10) > 0} />
            </LicenceRow>
        );
    };

    const renderLabRow = (item) => (
        <LicenceRow>
            <Grid item xs={12} md={5}>
                <TextField variant="outlined" size="small" fullWidth label="Lab" value={item.name || ''} disabled />
            </Grid>
            <Grid item xs={12} md={3}>
                <Typography variant="body2">{findOptionName(courseList, item.course_id) || '—'}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
                <TextField variant="outlined" size="small" fullWidth label="Licences" value={item?.count || 0} disabled />
            </Grid>
            <Grid item xs={6} md={2}>
                <AllocatedCell count={item?.user_count} total={item?.count} />
            </Grid>
        </LicenceRow>
    );

    const renderLicenceRow = (type, item, key) => {
        if (type === 'labs') return renderLabRow(item);
        if (type === 'courses') return renderCourseRow(item, key);
        return renderSelectableRow(type, item, key);
    };

    return (
        <React.Fragment>
            {showDetails && (
            <>
            <div className={classes.programmeSection}>
                {!formState.programme_type && !!project_id && (
                    <div className={classes.programmeHint}>Set the programme type for this project.</div>
                )}
                <div className={classes.programmeLabelRow}>
                    <Typography className={classes.programmeLabel}>Programme type *</Typography>
                    {programmeLocked && (
                        <span className={classes.lockedBadge}><LockOutlinedIcon aria-hidden="true" />Locked</span>
                    )}
                </div>
                <RadioGroup
                    name="programme_type"
                    value={formState.programme_type || ''}
                    onChange={handleInputChange}
                    className={classes.programmeCards}
                    aria-label="Programme type"
                >
                    {programmeTypes.map(option => {
                        const isCurrent = formState.programme_type === option.value;
                        const lockedClass = programmeLocked
                            ? ` ${classes.programmeCardLocked} ${isCurrent ? classes.programmeCardLockedCurrent : classes.programmeCardLockedOther}`
                            : '';
                        let highlight;
                        if (!programmeLocked && isCurrent) highlight = { borderColor: '#3D7DDD', backgroundColor: 'rgba(61, 125, 221, 0.1)' };
                        else if (!programmeLocked && fieldErrors.programme_type) highlight = { borderColor: '#dc2626' };
                        return (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                disabled={programmeLocked}
                                className={`${classes.programmeCard}${lockedClass}`}
                                style={highlight}
                                title={programmeLocked ? 'Programme type is locked for this project' : undefined}
                                control={<Radio color="primary" className={programmeLocked ? `${classes.radio} ${classes.radioLocked}` : classes.radio} disableRipple />}
                                label={(
                                    <span className={classes.programmeCardText}>
                                        <span className={classes.programmeCardTitle}>{option.label}</span>
                                        <span className={classes.programmeCardDescription}>{option.description}</span>
                                    </span>
                                )}
                            />
                        );
                    })}
                </RadioGroup>
                {fieldErrors.programme_type ? <FormHelperText error>{fieldErrors.programme_type}</FormHelperText> : null}
                <Typography className={classes.programmeHelper}>
                    Sets which optional profile fields users are asked to complete and which bulk upload template is used.
                </Typography>
                {programmeLocked && (
                    <Typography className={`${classes.programmeHelper} ${classes.lockedNote}`} style={{ marginTop: 0 }}>
                        <LockOutlinedIcon aria-hidden="true" />
                        Programme type can&apos;t be changed after users have been added to this project.
                    </Typography>
                )}
                <Grid container spacing={3}>
                    {formState.programme_type === YOUTH_PROGRAMME && (
                        <Grid item xs={12} md={4}>
                            <OutlinedSelect
                                val={formState.programme_subtype || ''}
                                name="programme_subtype"
                                label="Programme sub-type *"
                                variant="outlined"
                                disabled={programmeLocked}
                                error={!!fieldErrors.programme_subtype}
                                helperText={fieldErrors.programme_subtype}
                                handleChange={handleInputChange}
                                styleOverrides={{ width: '100%', marginTop: 0 }}
                                options={programmeSubtypes.map(option => ({ id: option.value, name: option.label }))}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            value={selectedProvinces}
                            options={deliveryProvinces}
                            getOptionLabel={option => option?.name || ''}
                            getOptionSelected={(option, selected) => option.code === selected.code}
                            onChange={(event, values) => handleInputChange({
                                target: { name: 'delivery_provinces', value: values.map(option => option.code) },
                            })}
                            renderOption={(option, { selected }) => (
                                <>
                                    <Checkbox color="primary" checked={selected} style={{ marginRight: 8 }} />
                                    {option.name}
                                </>
                            )}
                            renderTags={(values, getTagProps) => values.map((option, index) => (
                                <Chip size="small" label={option.name} {...getTagProps({ index })} />
                            ))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    label="Delivery province *"
                                    error={!!fieldErrors.delivery_provinces}
                                    helperText={fieldErrors.delivery_provinces}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <OutlinedSelect
                            val={formState.delivery_mode || ''}
                            name="delivery_mode"
                            label="Delivery mode *"
                            placeholder="Select mode"
                            variant="outlined"
                            error={!!fieldErrors.delivery_mode}
                            helperText={fieldErrors.delivery_mode}
                            handleChange={handleInputChange}
                            styleOverrides={{ width: '100%', marginTop: 0 }}
                            options={deliveryModes.map(option => ({ id: option.code, name: option.name }))}
                        />
                    </Grid>
                </Grid>
            </div>
            {!!project_id && !!formState.is_recruitment_project && (
                <Grid container style={{ marginTop: 8 }}>
                    <Grid item xs={12}>
                        <div className={classes.recruitmentBanner}>
                            <Typography className={classes.recruitmentBannerTitle}>
                                This is a recruitment project
                            </Typography>
                            <Typography className={classes.recruitmentBannerSubtitle}>
                                Enable Talent Access Email: {formState.enable_talent_access_email ? 'Enabled' : 'Disabled'}
                            </Typography>
                        </div>
                    </Grid>
                </Grid>
            )}
            <Grid container spacing={3} style={{ marginTop: 8 }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        label="Project Name"
                        value={formState.project_name}
                        name="project_name"
                        onChange={handleInputChange}
                        error={!!fieldErrors.project_name}
                        helperText={fieldErrors.project_name}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <OutlinedSelect
                        val={formState.company_id}
                        name="company_id"
                        label="Company Name"
                        variant="outlined"
                        handleChange={handleInputChange}
                        disabled={(customerList || []).length === 1 && formState.company_id === customerList[0]?.uuid}
                        error={!!fieldErrors.company_id}
                        helperText={fieldErrors.company_id}
                        styleOverrides={{ width: '100%', marginTop: 0 }}
                        options={customerList}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <OutlinedSelect
                        val={formState.company_admin_id}
                        name="company_admin_id"
                        label="Company Admin"
                        variant="outlined"
                        handleChange={handleInputChange}
                        error={!!fieldErrors.company_admin_id}
                        helperText={fieldErrors.company_admin_id}
                        styleOverrides={{ width: '100%', marginTop: 0 }}
                        options={customerAdminList}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <OutlinedSelect
                        val={formState.status}
                        name="status"
                        label="Status"
                        variant="outlined"
                        disabled={project_id ? false : true}
                        handleChange={handleInputChange}
                        styleOverrides={{ width: '100%', marginTop: 0 }}
                        options={["Open", "Complete"]}
                    />
                </Grid>
            </Grid>
            <div className={classes.adminPanel}>
                <Typography className={classes.adminTitle}>Project administrator</Typography>
                <RadioGroup row name="adminMode" value={adminMode} onChange={handleAdminMode}>
                    <FormControlLabel
                        value="existing"
                        className={classes.adminOption}
                        control={<Radio color="primary" className={classes.radio} disableRipple />}
                        label="Use a client administrator"
                    />
                    <FormControlLabel
                        value="invite"
                        className={classes.adminOption}
                        control={<Radio color="primary" className={classes.radio} disableRipple />}
                        label="Invite a new project administrator"
                    />
                </RadioGroup>
                {adminMode === 'existing' ? (
                    <div className={classes.inviteFields}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <OutlinedSelect
                                    val={formState.project_admin}
                                    name="project_admin"
                                    label="Client administrator"
                                    variant="outlined"
                                    handleChange={handleInputChange}
                                    styleOverrides={{ width: '100%', marginTop: 0 }}
                                    options={projectAdminList}
                                />
                            </Grid>
                        </Grid>
                    </div>
                ) : (
                    <div className={classes.inviteFields}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    label="First name *"
                                    name="firstName"
                                    value={invite.firstName}
                                    onChange={handleInviteChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    label="Surname *"
                                    name="lastName"
                                    value={invite.lastName}
                                    onChange={handleInviteChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    label="Work email *"
                                    name="email"
                                    type="email"
                                    value={invite.email}
                                    onChange={handleInviteChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <PhoneInputField
                                    label="Mobile number *"
                                    country="za"
                                    value={String(invite.contact_no || '').replace(/^\+/, '')}
                                    handleChange={(value) => {
                                        createdAdminId.current = '';
                                        setInvite(prev => ({
                                            ...prev,
                                            contact_no: value ? `+${String(value).replace(/^\+/, '')}` : '',
                                        }));
                                        markDraft();
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    label="Location *"
                                    name="location"
                                    value={invite.location}
                                    onChange={handleInviteChange}
                                />
                            </Grid>
                        </Grid>
                    </div>
                )}
            </div>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        label="Description"
                        value={formState.description}
                        name="description"
                        onChange={handleInputChange}
                        multiline
                        rows={4}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    {project_id && formState.is_recruitment_project ? (
                        // For recruitment projects, show read-only info in the banner above instead of a toggle
                        null
                    ) : (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!formState.enable_talent_access_email}
                                    onChange={handleInputChange}
                                    color="primary"
                                    name="enable_talent_access_email"
                                />
                            }
                            label="Enable Talent Access Email"
                            labelPlacement="end"
                        />
                    )}
                </Grid>
            </Grid>
            {showLearningAccess && (
                <div id={`learning-hub-${project_id}`}>
                    <LearningAccessSection
                        projectId={project_id}
                        learningAccess={learningAccess}
                        readOnly={!isSuperAdmin}
                    />
                </div>
            )}
            {showPendingLearningAccess && (
                <LearningAccessSection
                    pending
                    plans={pendingLearningAccess.plans}
                    value={pendingLearningAccess.value}
                    onChange={pendingLearningAccess.onChange}
                />
            )}
            </>
            )}
            {showLicences && (
            <>
            {section === 'all' && (
            <Grid container spacing={4} style={{marginTop: 20}}>
                <Grid item xs={12}>
                    <Typography className={classes.title} color="textSecondary" gutterBottom>Assign licenses</Typography>
                </Grid>
            </Grid>
            )}
            <LicenceTabsPanel
                tabs={licenceTabs}
                items={assignLicenseForm}
                isRowSelected={isRowSelected}
                getRowLabel={getRowLabel}
                renderRow={renderLicenceRow}
                onAdd={handleAddAssignLicence}
            />
            </>
            )}
            {showActions && (
            <div className={classes.placeBtn}>
                <Button onClick={handleBack} variant="outlined" color="default" className={classes.cancelBtn}>
                    Back
                </Button>
                <div className={classes.placeBtnEnd}>
                    <Button onClick={handleCancelCreate} variant="outlined" color="default" className={classes.cancelBtn}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        className={classes.btn}
                        disabled={
                            !formState.project_name ||
                            !formState.company_id ||
                            !formState.company_admin_id
                        }
                        onClick={formValidationCheck}>
                        {loading ? <CircularProgress size={15} color="secondary" /> : 'Next'}
                    </Button>
                </div>
            </div>
            )}
        </React.Fragment>
    );
});

CreateProjectForm.displayName = 'CreateProjectForm';
CreateProjectForm.propTypes = {
    handleInputChange: PropTypes.func.isRequired,
    formState: PropTypes.object.isRequired,
    customerId: PropTypes.string,
    onProjectAdminCreated: PropTypes.func,
    onDraftChange: PropTypes.func,
    learningAccess: PropTypes.object,
    programmeOptions: PropTypes.object,
    fieldErrors: PropTypes.objectOf(PropTypes.string),
    pendingLearningAccess: PropTypes.shape({
        value: PropTypes.object,
        plans: PropTypes.array,
        onChange: PropTypes.func,
    }),
    section: PropTypes.oneOf(['all', 'details', 'licences']),
}
export default CreateProjectForm;
